import { css } from 'astroturf';
import { StaticImage } from 'gatsby-plugin-image';
import React, { memo, useEffect, useRef, useState } from 'react';

import { trackEvent, secondsOnPage } from '../utils/track';

const styles = css`
  .tabs {
    position: relative;
    max-width: 1440px;
    margin: 0 auto;
    display: flex;
    justify-content: space-between;

    @media (width < 1280px) {
      display: none;
    }

    button {
      position: relative;
      flex: 1 1 auto;
      font-size: 0.875rem;
      font-weight: 600;
      background: transparent;
      border: none;
      color: rgb(10 21 48 / 60%);
      cursor: pointer;
      padding: 1rem 0;
      transition: all 0.3s ease-in-out;
      border-bottom: 5px solid transparent;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;

      &:hover,
      &:focus-visible {
        outline: none;
        border-color: color-mix(in srgb, var(--iconColor) 50%, transparent 50%);
        svg {
          color: var(--iconColor);
          transform: scale(1);
          transition: all 0.3s ease-in-out;
        }
      }

      &[aria-selected='true'] {
        color: rgb(10 21 48);
        border-color: color-mix(in srgb, var(--iconColor) 50%, transparent 50%);
        background: linear-gradient(
          to top,
          color-mix(in srgb, var(--iconColor) 10%, transparent 90%),
          transparent 100%
        );

        svg {
          color: var(--iconColor);
          transform: scale(1);
          transition: all 0.3s ease-in-out;
        }
      }

      &::after {
        position: absolute;
        top: 25%;
        right: 0;
        content: '';
        display: block;
        height: 50%;
        width: 1px;
        background: rgb(10 21 48 / 10%);
      }

      &:last-child::after {
        display: none;
      }

      svg {
        will-change: transform;

        transform: scale(0.8);
        transition: all 0.3s ease-in-out;
        color: inherit;
      }
    }
  }

  .progressBar {
    position: absolute;
    bottom: -5px;
    left: 0;
    height: 5px;
    width: 100%;
    background-color: var(--iconColor);
    transform-origin: left;
    transform: scaleX(0);
  }

  .wrapper {
    position: relative;
    display: grid;
    grid-template-areas: 'app';
    box-shadow: rgb(10 21 48 / 0.6) 0px 50px 50px -50px inset;

    background: linear-gradient(135deg, rgb(103, 178, 111), ease-in-out, rgb(60, 176, 255));

    &::before {
      pointer-events: none;
      content: '';
      display: block;
      position: absolute;
      top: -100px;
      width: 100%;
      height: 100%;
      padding: 2rem 0;
      opacity: 0.5;

      mask-image: radial-gradient(ellipse at center, #000 40%, transparent 95%);
      background-image: radial-gradient(#fff 1px, transparent 0);
      background-size: 40px 40px;
      background-position: -19px -19px;
    }

    @media (width < 1280px) {
      box-shadow: none;
      background: none;
    }

    .pauseBar {
      margin: 0 calc((100vw - min(1440px, 100vw - 6vw)) / 2);
      height: 50px;
      position: absolute;
      width: calc(100% - 6vw);
      max-width: 1440px;

      @media (width < 1280px) {
        display: none;
      }

      &[inert] {
        pointer-events: none;
        opacity: 0;
      }

      .mobileButton {
        background: red;
        position: absolute;
        top: 1.5rem;
        right: 0;
        background: transparent;
        width: 32px;
        height: 32px;
        border-radius: 36px;
        border: 3px solid #fff;
        color: #fff;
        cursor: pointer;
        display: grid;
        place-items: center;
        opacity: 0.66;
        transition: all 0.3s ease-in-out;

        svg {
          scale: 0.8;
          transition: all 0.3s ease-in-out;
        }

        &:hover,
        &:focus-visible {
          opacity: 1;
          background: #fff;
          color: #000;

          svg {
            scale: 1;
          }
        }
      }
    }
  }

  .appWrapper {
    grid-area: app;
    z-index: 3;
    margin: 5rem auto;
    position: relative;
    width: calc(100% - 6vw);
    max-width: 1440px;
    opacity: 0;
    transform: translateY(0px);
    transition: all 0.3s ease-in-out;
    will-change: opacity, transform;

    &.active {
      opacity: 1;
      transform: translateY(0) scale(1);
      transition: all 0.3s ease-in-out;
      will-change: opacity, transform;
    }

    @media (width < 1280px) {
      margin: 0 auto;
    }
  }

  .app {
    width: 100%;
    border-radius: 10px;
    overflow: hidden;
    box-shadow:
      rgba(255, 255, 255, 0.25) 0px 1px 1px 0px inset,
      rgba(50, 50, 93, 0.6) 0px 50px 100px -20px,
      rgba(0, 0, 0, 0.7) 0px 30px 60px -30px,
      rgba(103, 178, 111, 0.6) -10px 10px 60px -10px;
  }
`;

const tabs = [
  {
    name: 'Every breakpoint, side by side',
    image: '../../assets/images/hero/sync.png',
    color: 'rgb(103, 178, 111)',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="icon icon-tabler icons-tabler-outline icon-tabler-layout-cards"
      >
        <path d="M4 4m0 2a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z" />
        <path d="M14 4m0 2a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v6a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z" />
      </svg>
    ),
  },
  {
    name: 'See what each user sees',
    image: '../../assets/images/hero/emulate.png',
    color: 'rgb(98, 178, 143)',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        className="icon icon-tabler icons-tabler-outline icon-tabler-brightness"
      >
        <path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" />
        <path d="M12 3l0 18" />
        <path d="M12 9l4.65 -4.65" />
        <path d="M12 14.3l7.37 -7.37" />
        <path d="M12 19.6l8.85 -8.85" />
      </svg>
    ),
  },
  {
    name: 'Accessibility checked as you build',
    image: '../../assets/images/hero/a11y.png',
    color: 'rgb(98, 178, 143)',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="icon icon-tabler icons-tabler-outline icon-tabler-accessible"
      >
        <path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" />
        <path d="M10 16.5l2 -3l2 3m-2 -3v-2l3 -1m-6 0l3 1" />
        <circle cx="12" cy="7.5" r=".5" fill="currentColor" />
      </svg>
    ),
  },
  {
    name: 'Link previews before you ship',
    image: '../../assets/images/hero/meta.png',
    color: 'rgb(84, 177, 197)',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="icon icon-tabler icons-tabler-outline icon-tabler-social"
      >
        <path d="M12 5m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
        <path d="M5 19m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
        <path d="M19 19m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
        <path d="M12 14m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" />
        <path d="M12 7l0 4" />
        <path d="M6.7 17.8l2.8 -2" />
        <path d="M17.3 17.8l-2.8 -2" />
      </svg>
    ),
  },
  {
    name: 'Edit once, see it everywhere',
    image: '../../assets/images/hero/edit.png',
    color: 'rgb(69, 176, 238)',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="icon icon-tabler icons-tabler-outline icon-tabler-edit"
      >
        <path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1" />
        <path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z" />
        <path d="M16 5l3 3" />
      </svg>
    ),
  },
];

const Hero3D = () => {
  const [tabState, setTabState] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isReducedMotion, setIsReducedMotion] = useState(false);
  const [visitedTabs, setVisitedTabs] = useState([0]);
  const startTimeRef = useRef(null);
  const pauseStartTimeRef = useRef(null);
  const totalPausedTimeRef = useRef(0);
  const animationFrameRef = useRef(null);
  const progressBarRefs = useRef([]);
  const tabButtonRefs = useRef([]);

  const duration = 20000; // 20 seconds

  const resetProgress = (index) => {
    const progressBar = progressBarRefs.current[index];

    if (progressBar) {
      progressBar.style.transform = 'scaleX(0)';
    }
  };

  const resetAnimation = (index) => {
    startTimeRef.current = null;
    totalPausedTimeRef.current = 0;
    pauseStartTimeRef.current = null;
    resetProgress(index);
  };

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setIsReducedMotion(mediaQuery.matches);

    const handleChange = () => {
      setIsReducedMotion(mediaQuery.matches);
      cancelAnimationFrame(animationFrameRef.current);
    };
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      cancelAnimationFrame(animationFrameRef.current);
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  useEffect(() => {
    setVisitedTabs((currentVisitedTabs) => {
      if (currentVisitedTabs.includes(tabState)) {
        return currentVisitedTabs;
      }

      return [...currentVisitedTabs, tabState];
    });
    resetAnimation(tabState);
  }, [tabState]);

  useEffect(() => {
    if (isPaused || isReducedMotion) {
      cancelAnimationFrame(animationFrameRef.current);
      return undefined;
    }

    const activeTab = tabState;

    const updateProgress = (timestamp) => {
      if (startTimeRef.current === null) {
        startTimeRef.current = timestamp;
      }

      const elapsedTime = timestamp - startTimeRef.current - totalPausedTimeRef.current;
      const scalar = Math.min(Math.max(elapsedTime / duration, 0), 1);
      const progressBar = progressBarRefs.current[activeTab];

      if (progressBar) {
        progressBar.style.transform = `scaleX(${scalar})`;
      }

      if (scalar >= 1) {
        setTabState((prev) => (prev + 1) % tabs.length);
        return;
      }

      animationFrameRef.current = requestAnimationFrame(updateProgress);
    };

    animationFrameRef.current = requestAnimationFrame(updateProgress);

    return () => {
      cancelAnimationFrame(animationFrameRef.current);
    };
  }, [isPaused, isReducedMotion, tabState]);

  // Only deliberate interactions are tracked. The carousel advances on its own
  // every 10 seconds, and recording those would drown out the signal we
  // actually want: which feature people choose to look at.
  const trackTabInteraction = (index, method) => {
    trackEvent('Hero tab', {
      tab: tabs[index] ? tabs[index].name : String(index),
      position: index,
      method,
      seconds: secondsOnPage(),
    });
  };

  const handleTabClick = (index) => {
    trackTabInteraction(index, index === tabState ? 'replay' : 'click');

    if (index === tabState) {
      resetAnimation(index);
      return;
    }

    setTabState(index);
  };

  const startPause = () => {
    if (!isPaused) {
      setIsPaused(true);
      cancelAnimationFrame(animationFrameRef.current);
      pauseStartTimeRef.current = performance.now();
      // Pausing means someone wanted to keep looking at this tab — a strong
      // signal that the auto-advance is getting in their way.
      trackEvent('Hero tab', { tab: tabs[tabState] ? tabs[tabState].name : String(tabState), method: 'pause' });
    }
  };

  const endPause = () => {
    if (isPaused) {
      setIsPaused(false);

      if (pauseStartTimeRef.current !== null) {
        totalPausedTimeRef.current += performance.now() - pauseStartTimeRef.current;
      }

      pauseStartTimeRef.current = null;
    }
  };

  const handleKeyPress = (event) => {
    if (['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) {
      let newTabState = tabState;

      if (event.key === 'ArrowRight') {
        newTabState = (tabState + 1) % tabs.length;
      } else if (event.key === 'ArrowLeft') {
        newTabState = (tabState - 1 + tabs.length) % tabs.length;
      } else if (event.key === 'Home') {
        newTabState = 0;
        event.preventDefault();
      } else if (event.key === 'End') {
        newTabState = tabs.length - 1;
        event.preventDefault();
      }

      trackTabInteraction(newTabState, 'keyboard');
      setTabState(newTabState);
      tabButtonRefs.current[newTabState]?.focus();
    }
  };

  return (
    <div className={styles.hero}>
      <div className={styles.tabs} role="tablist" id="herotabs" onKeyDown={handleKeyPress} tabIndex={0}>
        {tabs.map((tab, index) => (
          <button
            role="tab"
            id={`tab-${index}`}
            aria-selected={tabState === index}
            tabIndex={tabState === index ? 0 : -1}
            aria-controls={`tabpanel-${index}`}
            type="button"
            key={tab.image}
            style={{
              '--iconColor': tab.color,
            }}
            ref={(element) => {
              tabButtonRefs.current[index] = element;
            }}
            onClick={() => handleTabClick(index)}
          >
            {tab.icon}
            {tab.name}
            <div
              className={styles.progressBar}
              ref={(element) => {
                progressBarRefs.current[index] = element;
              }}
              style={{
                visibility: tabState === index ? 'visible' : 'hidden',
              }}
            />
          </button>
        ))}
      </div>

      <div className={styles.wrapper}>
        <div className={styles.pauseBar} inert={isReducedMotion ? 'true' : undefined}>
          <button
            type="button"
            onClick={isPaused ? endPause : startPause}
            className={styles.mobileButton}
            data-tip={isPaused ? 'Resume' : 'Pause'}
            data-place="left"
            aria-label={isPaused ? 'Resume' : 'Pause'}
            aria-controls="herotabs"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="icon icon-tabler icons-tabler-filled icon-tabler-player-pause"
            >
              {isPaused ? (
                <>
                  <path d="M6 4v16a1 1 0 0 0 1.524 .852l13 -8a1 1 0 0 0 0 -1.704l-13 -8a1 1 0 0 0 -1.524 .852z" />
                </>
              ) : (
                <>
                  <path d="M9 4h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h2a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2z" />
                  <path d="M17 4h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h2a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2z" />
                </>
              )}
            </svg>
          </button>
        </div>

        <div
          className={[styles.appWrapper, tabState === 0 ? styles.active : ''].join(' ')}
          inert={tabState !== 0 ? 'true' : undefined}
          role="tabpanel"
          id={`tabpanel-${0}`}
          tabIndex="0"
          aria-labelledby="tab-0"
        >
          {visitedTabs.includes(0) ? <Image1 /> : null}
        </div>
        <div
          className={[styles.appWrapper, tabState === 1 ? styles.active : ''].join(' ')}
          inert={tabState !== 1 ? 'true' : undefined}
          role="tabpanel"
          id={`tabpanel-${1}`}
          tabIndex="0"
          aria-labelledby="tab-1"
        >
          {visitedTabs.includes(1) ? <Image2 /> : null}
        </div>
        <div
          className={[styles.appWrapper, tabState === 2 ? styles.active : ''].join(' ')}
          inert={tabState !== 2 ? 'true' : undefined}
          role="tabpanel"
          id={`tabpanel-${2}`}
          tabIndex="0"
          aria-labelledby="tab-2"
        >
          {visitedTabs.includes(2) ? <Image3 /> : null}
        </div>
        <div
          className={[styles.appWrapper, tabState === 3 ? styles.active : ''].join(' ')}
          inert={tabState !== 3 ? 'true' : undefined}
          role="tabpanel"
          id={`tabpanel-${3}`}
          tabIndex="0"
          aria-labelledby="tab-3"
        >
          {visitedTabs.includes(3) ? <Image4 /> : null}
        </div>
        <div
          className={[styles.appWrapper, tabState === 4 ? styles.active : ''].join(' ')}
          inert={tabState !== 4 ? 'true' : undefined}
          role="tabpanel"
          id={`tabpanel-${4}`}
          tabIndex="0"
          aria-labelledby="tab-4"
        >
          {visitedTabs.includes(4) ? <Image5 /> : null}
        </div>
      </div>
    </div>
  );
};

export default memo(Hero3D);

const Image1 = memo(() => (
  <StaticImage
    src="../../assets/images/hero/sync.png"
    className={styles.app}
    alt="Polypane showing six different panes in one overview, emulating different devices and viewports"
    width="1440"
    loading="eager"
    decoding="sync"
    fetchpriority="high"
    placeholder="none"
    backgroundColor="#353447"
    quality="100"
  />
));
Image1.displayName = 'Image1';

const Image2 = memo(() => (
  <StaticImage
    src="../../assets/images/hero/emulate.png"
    className={styles.app}
    alt="Polypane showing the same site in light mode, dark mode, forced color mode and reduced motion. The emulation settings are open showing additional options."
    width="1440"
    breakpoints={[750, 1080, 1366, 1920]}
    loading="lazy"
    placeholder="none"
    backgroundColor="#353447"
    quality="100"
  />
));
Image2.displayName = 'Image2';

const Image3 = memo(() => (
  <StaticImage
    src="../../assets/images/hero/a11y.png"
    className={styles.app}
    alt="Polypane showing a single site with its landmarks highlighted as well as a focus trail rendered across the page. An opened sidebar shows the heading structure of the page."
    width="1440"
    breakpoints={[750, 1080, 1366, 1920]}
    loading="lazy"
    placeholder="none"
    backgroundColor="#353447"
    quality="100"
  />
));
Image3.displayName = 'Image3';

const Image4 = memo(() => (
  <StaticImage
    src="../../assets/images/hero/meta.png"
    className={styles.app}
    alt="Polypane with an opened sidebar showing the meta information of a page: title, description as wel as og tags and other meta data. The top of a social media card preview for Bluesky is visible."
    width="1440"
    breakpoints={[750, 1080, 1366, 1920]}
    loading="lazy"
    placeholder="none"
    backgroundColor="#353447"
    quality="100"
  />
));
Image4.displayName = 'Image4';

const Image5 = memo(() => (
  <StaticImage
    src="../../assets/images/hero/edit.png"
    className={styles.app}
    alt="Polypane showing three panes, with a sidebar containing the element inspector. The H1 element that's selected is highlighted in all panes. The sidebar shows the CSS properties and box model of the selected element."
    width="1440"
    breakpoints={[750, 1080, 1366, 1920]}
    loading="lazy"
    placeholder="none"
    backgroundColor="#353447"
    quality="100"
  />
));
Image5.displayName = 'Image5';
